import { Cancel as CancelIcon, Done as DoneIcon } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
} from '@mui/material'
import AsyncActionButton from '@src/shared/components/AsyncActionButton'
import { SimpleDatePicker } from '@src/shared/components/DatePicker'
import PageSpinner from '@src/shared/components/PageSpinner'
import SelectComponent from '@src/shared/components/SelectComponent'
import { ExceptionsFiltersDto } from '@src/tools/error-tracker/types/ExceptionsFiltersDto'
import { SelectValueListItem } from '@src/shared/types/SelectValueListItem'
import { memo, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface props {
  filterValues: ExceptionsFiltersDto
  open: boolean
  applicationNames: SelectValueListItem[]
  isFetching: boolean
  onApplyFilters: (filters: ExceptionsFiltersDto) => void
  onCancel: () => void
  handleClearFilter: () => void
}

const ExceptionsFiltersDialog = ({
  filterValues,
  open,
  onApplyFilters,
  applicationNames,
  isFetching,
  onCancel,
  handleClearFilter,
}: props) => {
  const { t } = useTranslation()

  const { register, handleSubmit, reset, control } = useForm<ExceptionsFiltersDto>({
    defaultValues: filterValues,
  })

  useEffect(() => {
    if (!open) {
      reset(filterValues)
    }
  }, [open, filterValues, reset])

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      maxWidth="md"
      keepMounted
      transitionDuration={0}
      disablePortal
    >
      <DialogTitle style={{ borderBottom: '1px solid' }} id="alert-dialog-title" sx={{ textAlign: 'center' }}>
        {t('common.filters')}
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{
            position: 'absolute',
            right: 15,
            top: 15,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit(onApplyFilters)}>
        <DialogContent>
          <PageSpinner isLoading={isFetching} />
          <Grid container direction="column" spacing={2} sx={{ mt: 5, width: 600 }}>
            <Grid>
              <SelectComponent
                valueList={applicationNames}
                label={t('common.applicationName')}
                {...register('applicationName')}
              />
            </Grid>
            <Grid justifyContent="center">
              <Stack justifyContent="space-between" direction="row">
                <SimpleDatePicker
                  label={t('common.startDate')}
                  sx={{ minWidth: '49%' }}
                  name="startDate"
                  control={control}
                />
              </Stack>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <AsyncActionButton startIcon={<CancelIcon />} onClick={handleClearFilter}>
            {t('common.reset')}
          </AsyncActionButton>
          <AsyncActionButton variant="contained" type="submit" startIcon={<DoneIcon />}>
            {t('common.apply')}
          </AsyncActionButton>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default memo(ExceptionsFiltersDialog)
